import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchMe } from "layouts/authentication/auth.thunk";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import ErrorAlert from "components/ErrorAlert";
import GoalButton from "layouts/goals/components/GoalButton";
import useRoles from "./useRoles";
import RolesTable from "./components/RolesTable";
import RoleFormModal from "./components/RoleFormModal";
import DeleteRoleModal from "./components/DeleteRoleModal";

// Roles of the organization: who can do what, limited to the features the
// organization has. Reached from the Users page, for Super Admins only.
function Roles() {
  const {
    auth: { admin, organization },
  } = useSelector((state) => state);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    roles,
    isLoading,
    errorMessage,
    successMessage,
    fetchAll,
    createRole,
    updateRole,
    deleteRole,
    clearError,
    clearSuccess,
  } = useRoles(admin?.id);

  const [formTarget, setFormTarget] = useState(null); // { role } ; null role = new
  const [toDelete, setToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchAll();
    // The features the editor offers are the organization's; make sure they are
    // current, in case someone changed them since this session loaded.
    if (admin?.id) dispatch(fetchMe(admin.id));
  }, [admin?.id]);

  const handleSave = async (body) => {
    setIsSubmitting(true);
    const ok = formTarget.role
      ? await updateRole(formTarget.role.id, body)
      : await createRole(body);
    setIsSubmitting(false);
    if (ok) setFormTarget(null);
  };

  const handleConfirmDelete = async () => {
    setIsSubmitting(true);
    const ok = await deleteRole(toDelete.id);
    setIsSubmitting(false);
    if (ok) setToDelete(null);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SuiBox py={3}>
        {errorMessage && (
          <ErrorAlert color="error" message={errorMessage} clearError={clearError} />
        )}
        {successMessage && (
          <ErrorAlert color="success" message={successMessage} clearError={clearSuccess} />
        )}
        <SuiBox mb={1}>
          <GoalButton
            variant="text"
            size="small"
            icon="arrow_back"
            onClick={() => navigate("/users")}
          >
            Users
          </GoalButton>
        </SuiBox>
        <SuiBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SuiTypography variant="h5" fontWeight="medium">
            Roles
          </SuiTypography>
          <GoalButton icon="add" onClick={() => setFormTarget({ role: null })}>
            Add role
          </GoalButton>
        </SuiBox>
        <RolesTable
          roles={roles}
          isLoading={isLoading}
          onEdit={(role) => setFormTarget({ role })}
          onDelete={setToDelete}
        />
        <RoleFormModal
          open={!!formTarget}
          onClose={() => setFormTarget(null)}
          onSave={handleSave}
          isSubmitting={isSubmitting}
          role={formTarget?.role || null}
          organizationFeatures={organization?.features}
        />
        <DeleteRoleModal
          open={!!toDelete}
          onClose={() => setToDelete(null)}
          onConfirm={handleConfirmDelete}
          roleName={toDelete?.name}
          isSubmitting={isSubmitting}
        />
      </SuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Roles;
